import React, { useEffect, useState } from 'react';
import { fetchTodaysLoginImage } from './loginImagesApi';

/**
 * Vänsterpanelen på inloggningssidan (desktop).
 *
 * Visar dagens inloggningsbild om organisationen aktiverat några, annars
 * standardgrafiken (blå gradient med ritningsmönster). Gradienten ligger
 * alltid underst, så panelen ser aldrig tom ut medan fotot laddas.
 * Med foto: mörk toning nedifrån och texten längst ned, så att vit text
 * förblir läsbar även mot ljusa bilder.
 */

function BlueprintPattern() {
  return (
    <svg className="absolute inset-0 opacity-25 text-blue-200 dark:text-gray-500" viewBox="0 0 400 400" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <pattern id="login-hero-grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#login-hero-grid)" />
      <g stroke="currentColor" strokeWidth="1" className="opacity-50 dark:opacity-60">
        <path d="M20 360 L140 300 L220 340 L360 280" fill="none" />
        <path d="M60 60 h280 v200 h-280 z" fill="none" />
        <circle cx="240" cy="140" r="36" fill="none" />
      </g>
    </svg>
  );
}

const SCRIM_STYLE = {
  background: 'linear-gradient(to top, rgba(2, 6, 23, 0.88) 0%, rgba(2, 6, 23, 0.55) 40%, rgba(2, 6, 23, 0.18) 75%, rgba(2, 6, 23, 0.08) 100%)',
};

export default function LoginHeroPanel() {
  const [image, setImage] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchTodaysLoginImage()
      .then((img) => {
        if (alive && img && img.url) setImage(img);
      })
      .catch(() => {
        /* nätverksfel: behåll standardgrafiken */
      });
    return () => {
      alive = false;
    };
  }, []);

  const showPhoto = Boolean(image) && !failed;

  return (
    <div className="relative overflow-hidden" data-login-hero={showPhoto ? 'photo' : 'blueprint'}>
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] via-[#2C5282] to-[#1E40AF] dark:from-[#0f172a] dark:via-[#1e293b] dark:to-[#0a0f1a]" />
      {!showPhoto && <BlueprintPattern />}
      {showPhoto && (
        <img
          src={image.url}
          alt=""
          aria-hidden="true"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
      {showPhoto && <div className="absolute inset-0" style={SCRIM_STYLE} aria-hidden="true" />}
      <div className={`relative z-10 h-full text-white p-6 md:p-8 xl:p-10 flex flex-col ${showPhoto ? 'justify-end' : 'justify-center'}`}>
        <div className="border-l-2 border-white/[0.65] dark:border-[#F97316]/[0.65] pl-4">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-[-0.01em] leading-[1.15] text-white dark:text-slate-100">
            Projektstyrning. På riktigt.
          </h2>
          <div className="mt-2 text-xl md:text-2xl font-medium leading-snug text-white/[0.9] dark:text-slate-100/[0.9]">
            Ett system. Hela projektet.
          </div>
          <p className="mt-5 max-w-[42ch] text-base font-normal leading-[1.5] text-white/[0.8] dark:text-slate-100/[0.75]">
            KanPlan samlar allt som krävs för att styra våra byggprojekt — från anbud och projektering till produktion, ekonomi, kvalitet och uppföljning — i ett och samma system.
          </p>
          <p className="mt-6 max-w-[42ch] text-sm font-medium leading-snug text-white/[0.7] dark:text-slate-100/[0.65]">
            Byggt av Kanonaden, för Kanonaden.
          </p>
        </div>
      </div>
    </div>
  );
}
